/* eslint-disable consistent-return */
import React from "react";
import { gql } from "@apollo/client";
import dynamic from "next/dynamic";

import { makeStyles } from "tss-react/mui";
import useTranslation from "next-translate/useTranslation";
import TimerIcon from "@mui/icons-material/Timer";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";

import Button from "components/CustomButtons/Button";

import {
  REVIEWS_ID,
  PRODUCT_PAGE_CONTENT_RESPONSIVE_SETTINGS,
} from "settings/global-website-config";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { hexToRgb } from "@mui/material";

import ReviewDetails from "components/Reviews/ReviewDetails";
import { useRenders } from "../../modules/utils";

import {
  reviewInfoLight,
  reviewInfoIcon,
} from "../../assets/jss/nextjs-material-kit-pro";

const CustomCarousel = dynamic(() =>
  import("components/Feedblocks/CustomCarousel.js")
);

const useStyles = makeStyles()((theme) => ({
  reviewInfoLight,
  reviewInfoIcon,
  reviewLinkOutButton: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    "&:hover": {
      backgroundColor: "unset",
      "& a,& svg": { color: theme.palette.common.white },
    },
  },
  reviewContainer: {
    background: `linear-gradient(60deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.common.white,
    "& a": { color: `rgba(${hexToRgb(theme.palette.common.white)}, 0.8)` },
  },
  reviewCarouselContainer: { margin: theme.spacing(1) },
  reviewNoCarouselContainer: { margin: theme.spacing(1, 0) },

  cardDescriptionWhite: {
    color: `rgba(${hexToRgb(theme.palette.common.white)}, 0.8)`,
  },
  cardTitleWhite: {
    "&, & a": {
      fontStyle: "italic",
      marginTop: ".625rem",
      marginBottom: ".625rem",
      minHeight: "auto",
      //  color: `${theme.palette.common.white} !important`,
    },
  },
}));

export const Reviews = React.memo((props) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  useRenders("reviews");

  const { expertReviews, productId, basic, noCarousel } = props;
  if (!expertReviews) return <div />;
  const innerReviews = expertReviews.map(
    (review) =>
      review.reviewDetails.writtenReview && (
        <Card
          className={cx({
            [classes.reviewContainer]: true,
            [classes.reviewCarouselContainer]: !noCarousel,
            [classes.reviewNoCarouselContainer]: noCarousel,
          })}
          variant="elevation"
          elevation={4}
          key={`${review.reviewDetails.reviewHeadline}-${review.reviewDetails.writtenReview.pageUrl}`}
        >
          <CardContent sx={{ p: 2, m: 0 }}>
            <ReviewDetails
              review={review}
              url={review.reviewDetails.writtenReview.pageUrl}
              productId={productId}
            />

            {review.reviewHeadline && (
              <h4 className={classes.cardTitleWhite}>
                {`"${review.reviewDetails.reviewHeadline}"`}
              </h4>
            )}
            {review.reviewDetails.writtenReview.excerpt && (
              <p className={classes.cardDescriptionWhite}>
                <em>{`“${review.reviewDetails.writtenReview.excerpt}”`}</em>
              </p>
            )}
          </CardContent>
          <CardActions sx={{ display: "block" }}>
            <Button
              className={classes.reviewLinkOutButton}
              variant="text"
              endIcon={<OpenInNewIcon />}
              sx={{ mb: 1 }}
            >
              <a
                href={review.reviewDetails.writtenReview.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("product:readReview")}
              </a>
            </Button>
            {review.reviewDetails.writtenReview.timeToRead && (
              <p
                className={`${classes.reviewInfoLight} ${classes.reviewInfoIcon}`}
              >
                <TimerIcon />
                {t("product:timeToRead", {
                  time: review.reviewDetails.writtenReview.timeToRead,
                })}
              </p>
            )}
          </CardActions>
        </Card>
      )
  );

  return (
    <div className={classes.reviewsWrapper} id={REVIEWS_ID}>
      {!basic && !noCarousel ? (
        <CustomCarousel
          responsiveSettings={{
            ...PRODUCT_PAGE_CONTENT_RESPONSIVE_SETTINGS,
            desktop: 2,
          }}
        >
          {innerReviews}
        </CustomCarousel>
      ) : (
        innerReviews
      )}
    </div>
  );
});
if (process.env.NODE_ENV !== "production") {
  Reviews.displayName = "Reviews";
}
// Now obsolete, kept for ref while migrating:

// There is some overlap between the below queries but I decided that it makes more readable code to keep them separate rather than try to have one incorporate the other

const REVIEWDETAILSFRAGMENT = gql`
  fragment ReviewDetailsFragment on Review {
    id
    reviewColour
    date
    reviewDetails {
      reviewedProducts {
        ... on Product {
          id
          uri
          title
        }
      }
      videoReview {
        url
        duration
        embeddable
        viewCount
        thumbnails {
          high {
            url
          }
        }
      }
      writtenReview {
        pageUrl
        wordCount
        review
        timeToRead
        excerpt
      }
      publisher {
        ... on NodeWithTitle {
          title
        }
        ... on Node {
          id
        }
      }
      sex {
        ... on Enum {
          id
          slug
          title
        }
      }
      scoreIsExplicit
      dateIsExplicit
      reviewScore
      reviewHeadline
      reviewer {
        ... on Athlete {
          id
          title
          slug
          uri
          athleteDetails {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const REVIEWCONTENTFRAGMENT = gql`
  fragment ReviewContentFragment on ContentNode {
    ... on Product {
      productReviews(withContentOnly: true, stati: PUBLISH) {
        reviewsScore
        reviewsCount
        reviewsCommentary
        expert {
          expertReviews {
            ...ReviewDetailsFragment
          }
        }
        athlete {
          publicReviews {
            ... on Review {
              id
            }
          }
          reviewsScore
          reviewsCount
        }
      }
      manufacturerVideos {
        nodes {
          ...ReviewDetailsFragment
        }
      }
    }
    ... on Family {
      productReviews {
        reviewsScore
        reviewsCount
        reviewsCommentary
      }
    }
  }
  ${REVIEWDETAILSFRAGMENT}
`;

// Removed from reviewFeedFragment
/* expert {
        expertReviews {
          ... on Review {
            id
            reviewDetails {
              videoReview {
                url
              }
              writtenReview {
                pageUrl
              }
            }
          }
        }
      } */
export const REVIEWSCORESFRAGMENT = gql`
  fragment ReviewScoresFragment on Product {
    productReviews {
      reviewsScore
      reviewsCount
      reviewsCommentary
    }
  }
`;
